import React, { useState } from 'react';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CompletedIcon,
  Cancel as CancelIcon,
  AccessTime as PendingIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { formatPrice, formatDate } from '../../utils/format';
import { firebaseOrdersService } from '../../services/firebaseService';
import { toast } from 'react-hot-toast';
import BackButton from '../../components/common/BackButton';

const AdminOrders = () => {
  const queryClient = useQueryClient();
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDetailOpen, setOrderDetailOpen] = useState(false);
  const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderToDelete, setOrderToDelete] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    search: '',
    dateFrom: '',
    dateTo: ''
  });

  // Query para obtener pedidos desde Firebase
  const { data: ordersData, isLoading: ordersLoading, error: ordersError } = useQuery({
    queryKey: ['firebaseOrders', filters],
    queryFn: async () => {
      const response = await firebaseOrdersService.getAll();
      let orders = response.data.data.filter(
        (order) => order.paymentStatus === 'paid'
      );

      // Aplicar filtros del lado cliente
      if (filters.status) {
        orders = orders.filter(order => order.status === filters.status);
      }
      
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        orders = orders.filter(order => 
          order.id.toString().includes(searchTerm) ||
          order.customerEmail?.toLowerCase().includes(searchTerm) ||
          order.shippingAddress?.email?.toLowerCase().includes(searchTerm) ||
          order.shippingAddress?.name?.toLowerCase().includes(searchTerm) ||
          order.customerName?.toLowerCase().includes(searchTerm)
        );
      }
      
      if (filters.dateFrom) {
        const fromDate = new Date(filters.dateFrom);
        orders = orders.filter(order => {
          const orderDate = order.createdAt?.toDate ? order.createdAt.toDate() : new Date(order.createdAt);
          return orderDate >= fromDate;
        });
      }
      
      if (filters.dateTo) {
        const toDate = new Date(filters.dateTo);
        toDate.setHours(23, 59, 59, 999); // Incluir todo el día
        orders = orders.filter(order => {
          const orderDate = order.createdAt?.toDate ? order.createdAt.toDate() : new Date(order.createdAt);
          return orderDate <= toDate;
        });
      }
      
      return { data: { orders } };
    }
  });

  // Query para obtener estadísticas desde Firebase
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['firebaseOrderStats'],
    queryFn: async () => {
      console.log('🔄 AdminOrders: Consultando estadísticas...');
      const result = await firebaseOrdersService.getStats();
      console.log('📊 AdminOrders: Estadísticas recibidas:', result);
      return result;
    }
  });

  // Mutation para actualizar estado de pedido en Firebase
  const updateStatusMutation = useMutation({
    mutationFn: ({ orderId, status }) => firebaseOrdersService.updateStatus(orderId, status),
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseOrders']);
      queryClient.invalidateQueries(['firebaseOrderStats']);
      toast.success('Estado del pedido actualizado exitosamente');
      setStatusUpdateOpen(false);
      setSelectedOrder(null);
    },
    onError: (error) => {
      toast.error(error.message || 'Error al actualizar el estado del pedido');
    }
  });

  // Mutation para eliminar pedido en Firebase
  const deleteOrderMutation = useMutation({
    mutationFn: (orderId) => firebaseOrdersService.delete(orderId),
    onSuccess: () => {
      queryClient.invalidateQueries(['firebaseOrders']);
      queryClient.invalidateQueries(['firebaseOrderStats']);
      toast.success('Pedido eliminado exitosamente');
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
    },
    onError: (error) => {
      toast.error(error.message || 'Error al eliminar el pedido');
      setDeleteDialogOpen(false);
      setOrderToDelete(null);
    }
  });

  const orders = ordersData?.data?.orders || [];
  const stats = statsData?.data?.data || {};

  // Helper para formatear fechas de Firebase
  const formatFirebaseDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    // Si es un timestamp de Firebase
    if (timestamp.toDate) {
      return formatDate(timestamp.toDate());
    }
    
    // Si es una fecha normal
    if (timestamp instanceof Date) {
      return formatDate(timestamp);
    }
    
    // Si es un string de fecha
    return formatDate(new Date(timestamp));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'confirmed':
        return 'info';
      case 'processing':
        return 'primary';
      case 'shipped':
        return 'secondary';
      case 'delivered':
        return 'success';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <PendingIcon />;
      case 'confirmed':
      case 'processing':
        return <EditIcon />;
      case 'shipped':
        return <ShippingIcon />;
      case 'delivered':
        return <CompletedIcon />;
      case 'cancelled':
        return <CancelIcon />;
      default:
        return <PendingIcon />;
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmado',
      processing: 'Procesando',
      shipped: 'Enviado',
      delivered: 'Entregado',
      cancelled: 'Cancelado'
    };
    return labels[status] || status;
  };

  const handleViewOrder = (order) => {
    setSelectedOrder(order);
    setOrderDetailOpen(true);
  };

  const handleUpdateStatus = (order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setStatusUpdateOpen(true);
  };

  const handleConfirmStatusUpdate = () => {
    if (selectedOrder && newStatus) {
      updateStatusMutation.mutate({
        orderId: selectedOrder.id,
        status: newStatus
      });
    }
  };

  const handleDeleteOrder = (order) => {
    setOrderToDelete(order);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (orderToDelete) {
      deleteOrderMutation.mutate(orderToDelete.id);
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setOrderToDelete(null);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      search: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <BackButton />
      
      <Typography 
        variant="h3" 
        sx={{ 
          mb: 4, 
          fontFamily: '"Playfair Display", serif',
          fontWeight: 600 
        }}
      >
        Gestión de Pedidos
      </Typography>

      {/* Estadísticas */}
      {statsLoading ? (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography>Cargando estadísticas...</Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Pedidos
                </Typography>
                <Typography variant="h4">
                  {stats.totalOrders || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Pedidos Pendientes
                </Typography>
                <Typography variant="h4" color="warning.main">
                  {stats.pendingOrders || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Pedidos Completados
                </Typography>
                <Typography variant="h4" color="success.main">
                  {stats.completedOrders || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Ingresos Totales
                </Typography>
                <Typography variant="h4" color="primary">
                  {formatPrice(stats.totalRevenue || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Botones de debugging (solo en desarrollo) */}
      

      {/* Filtros */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterIcon />
            Filtros de Búsqueda
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Buscar pedido"
                placeholder="ID, email, nombre..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={filters.status}
                  label="Estado"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">Todos</MenuItem>
                  <MenuItem value="pending">Pendiente</MenuItem>
                  <MenuItem value="confirmed">Confirmado</MenuItem>
                  <MenuItem value="processing">Procesando</MenuItem>
                  <MenuItem value="shipped">Enviado</MenuItem>
                  <MenuItem value="delivered">Entregado</MenuItem>
                  <MenuItem value="cancelled">Cancelado</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                InputLabelProps={{ shrink: true }}
                value={filters.dateFrom}
                onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                InputLabelProps={{ shrink: true }}
                value={filters.dateTo}
                onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                variant="outlined"
                onClick={clearFilters}
                sx={{ mr: 1 }}
              >
                Limpiar Filtros
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Tabla de pedidos */}
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Lista de Pedidos
          </Typography>
          
          {ordersLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : ordersError ? (
            <Alert severity="error" sx={{ my: 2 }}>
              Error al cargar los pedidos: {ordersError.message}
            </Alert>
          ) : orders.length === 0 ? (
            <Alert severity="info" sx={{ my: 2 }}>
              No se encontraron pedidos con los filtros aplicados
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Total</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          #{order.id.toString().padStart(6, '0')}
                        </Typography>
                      </TableCell>
                      <TableCell>{order.customerName || order.shippingAddress?.name || 'N/A'}</TableCell>
                      <TableCell>{order.customerEmail || order.shippingAddress?.email || 'N/A'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {formatPrice(order.total)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={getStatusIcon(order.status)}
                          label={getStatusLabel(order.status)}
                          color={getStatusColor(order.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatFirebaseDate(order.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Ver detalles">
                          <IconButton 
                            onClick={() => handleViewOrder(order)}
                            size="small"
                          >
                            <ViewIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cambiar estado">
                          <IconButton 
                            onClick={() => handleUpdateStatus(order)}
                            size="small"
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar pedido">
                          <IconButton 
                            onClick={() => handleDeleteOrder(order)}
                            size="small"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Dialog de detalles del pedido */}
      <Dialog 
        open={orderDetailOpen} 
        onClose={() => setOrderDetailOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Detalles del Pedido #{selectedOrder?.id?.toString().padStart(6, '0')}
        </DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Información del Cliente</Typography>
                  <Typography><strong>Nombre:</strong> {selectedOrder.customerName || selectedOrder.shippingAddress?.name}</Typography>
                  <Typography><strong>Email:</strong> {selectedOrder.customerEmail || selectedOrder.shippingAddress?.email}</Typography>
                  <Typography><strong>Teléfono:</strong> {selectedOrder.shippingAddress?.phone}</Typography>
                  <Typography><strong>Dirección:</strong> {selectedOrder.shippingAddress?.street}</Typography>
                  <Typography><strong>Ciudad:</strong> {selectedOrder.shippingAddress?.city}</Typography>
                  <Typography><strong>Código Postal:</strong> {selectedOrder.shippingAddress?.postalCode}</Typography>
                  {selectedOrder.shippingAddress?.province && (
                    <Typography><strong>Provincia:</strong> {selectedOrder.shippingAddress.province}</Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>Información del Pedido</Typography>
                  <Typography><strong>Estado:</strong> 
                    <Chip
                      icon={getStatusIcon(selectedOrder.status)}
                      label={getStatusLabel(selectedOrder.status)}
                      color={getStatusColor(selectedOrder.status)}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  <Typography><strong>Fecha:</strong> {formatFirebaseDate(selectedOrder.createdAt)}</Typography>
                  <Typography><strong>Método de Pago:</strong> {selectedOrder.paymentMethod || '—'}</Typography>
                  <Typography><strong>Estado del pago:</strong>
                    <Chip
                      label={selectedOrder.paymentStatus === 'paid' ? 'Pagado' : 'Sin confirmar'}
                      color={selectedOrder.paymentStatus === 'paid' ? 'success' : 'warning'}
                      size="small"
                      sx={{ ml: 1 }}
                    />
                  </Typography>
                  {selectedOrder.paymentIntentId && (
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                      <strong>ID Stripe:</strong> {selectedOrder.paymentIntentId}
                    </Typography>
                  )}
                  {selectedOrder.paidAt && (
                    <Typography><strong>Pagado el:</strong> {formatFirebaseDate(selectedOrder.paidAt)}</Typography>
                  )}
                  <Typography><strong>Subtotal:</strong> {formatPrice(selectedOrder.subtotal)}</Typography>
                  <Typography><strong>Envío:</strong> {formatPrice(selectedOrder.shipping)} ({selectedOrder.shippingRateName || '—'})</Typography>
                  {selectedOrder.shippingDetails && (
                    <>
                      <Typography>
                        <strong>Zona:</strong>{' '}
                        {selectedOrder.shippingDetails.zoneLabel || selectedOrder.shippingZone || '—'}
                      </Typography>
                      <Typography>
                        <strong>Peso:</strong> {selectedOrder.shippingDetails.weightKg?.toFixed(2)} kg (
                        {selectedOrder.shippingDetails.bottles} botellas)
                      </Typography>
                      {selectedOrder.shippingDetails.bracketLabel && (
                        <Typography>
                          <strong>Tarifa:</strong> {selectedOrder.shippingDetails.bracketLabel}
                          {selectedOrder.shippingDetails.freeShipping && ' (envío gratis aplicado)'}
                        </Typography>
                      )}
                    </>
                  )}
                  {!selectedOrder.shippingDetails && (
                    <Typography><strong>Zona de envío:</strong> {selectedOrder.shippingZone || '—'}</Typography>
                  )}
                  {selectedOrder.discount > 0 && (
                    <Typography><strong>Descuento:</strong> -{formatPrice(selectedOrder.discount)} {selectedOrder.discountCode ? `(${selectedOrder.discountCode})` : ''}</Typography>
                  )}
                  <Typography><strong>Total:</strong> {formatPrice(selectedOrder.total)}</Typography>
                  {selectedOrder.notes && (
                    <Typography><strong>Notas:</strong> {selectedOrder.notes}</Typography>
                  )}
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>Productos</Typography>
              <TableContainer component={Paper} sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Producto</TableCell>
                      <TableCell align="center">Cantidad</TableCell>
                      <TableCell align="right">Precio Unit.</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedOrder.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.wineName}</TableCell>
                        <TableCell align="center">{item.quantity}</TableCell>
                        <TableCell align="right">{formatPrice(item.price)}</TableCell>
                        <TableCell align="right">{formatPrice(item.price * item.quantity)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrderDetailOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de cambio de estado */}
      <Dialog 
        open={statusUpdateOpen} 
        onClose={() => setStatusUpdateOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Cambiar Estado del Pedido #{selectedOrder?.id?.toString().padStart(6, '0')}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Nuevo Estado</InputLabel>
            <Select
              value={newStatus}
              label="Nuevo Estado"
              onChange={(e) => setNewStatus(e.target.value)}
            >
              <MenuItem value="pending">Pendiente</MenuItem>
              <MenuItem value="confirmed">Confirmado</MenuItem>
              <MenuItem value="processing">Procesando</MenuItem>
              <MenuItem value="shipped">Enviado</MenuItem>
              <MenuItem value="delivered">Entregado</MenuItem>
              <MenuItem value="cancelled">Cancelado</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusUpdateOpen(false)}>Cancelar</Button>
          <Button 
            onClick={handleConfirmStatusUpdate}
            variant="contained"
            disabled={updateStatusMutation.isPending || !newStatus}
          >
            {updateStatusMutation.isPending ? 'Actualizando...' : 'Actualizar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmación de eliminación */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Confirmar Eliminación
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            ¿Estás seguro de que deseas eliminar el pedido #{orderToDelete?.id?.toString().padStart(6, '0')}?
          </Typography>
          {orderToDelete && (
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2"><strong>Cliente:</strong> {orderToDelete.customerName || orderToDelete.shippingAddress?.name}</Typography>
              <Typography variant="body2"><strong>Email:</strong> {orderToDelete.customerEmail || orderToDelete.shippingAddress?.email}</Typography>
              <Typography variant="body2"><strong>Total:</strong> {formatPrice(orderToDelete.total)}</Typography>
              <Typography variant="body2"><strong>Estado:</strong> {getStatusLabel(orderToDelete.status)}</Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            <strong>⚠️ Esta acción no se puede deshacer.</strong><br/>
            El pedido será eliminado permanentemente de Firebase.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>Cancelar</Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            disabled={deleteOrderMutation.isPending}
          >
            {deleteOrderMutation.isPending ? 'Eliminando...' : 'Eliminar Definitivamente'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminOrders; 